import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";
import { FormActions } from "@jbnl/shared-ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SetPasswordStatus } from "@/features/session/localAccount";
import {
  accountKey,
  useBeginTweedeFactor,
  useChangeWachtwoord,
  useRemoveWachtwoord,
  useSetWachtwoord,
} from "./useAccount";

type Vertaler = ReturnType<typeof useTranslation<["account", "common"]>>["t"];

const ERROR_KEY = {
  "verkeerde-code": "wachtwoord.errors.verkeerde-code",
  geblokkeerd: "wachtwoord.errors.geblokkeerd",
  datalek: "wachtwoord.errors.datalek",
  "al-gezet": "wachtwoord.errors.al-gezet",
  geweigerd: "wachtwoord.errors.geweigerd",
} as const satisfies Record<
  Exclude<SetPasswordStatus, "gezet" | "verwijderd">,
  string
>;

/** Looked up rather than interpolated into a key, so an unknown API status cannot compile. */
function message(status: SetPasswordStatus, t: Vertaler) {
  return status === "gezet" || status === "verwijderd"
    ? ""
    : t(ERROR_KEY[status], { mailbox: t("common:mailbox") });
}

/** The API's own message wins; a rejected request without one falls back to a generic key. */
function foutmeldingUitOnbekendeFout(cause: unknown, fallback: string) {
  return cause instanceof Error ? cause.message : fallback;
}

/**
 * Shown once, on the response that created them - there is no endpoint that hands them back,
 * so nothing may refetch this card until the holder says they have them.
 */
function HerstelcodesKaart({
  codes,
  onBevestig,
  t,
}: {
  codes: string[];
  onBevestig: () => void;
  t: Vertaler;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">{t("wachtwoord.done.title")}</p>
      <p className="text-sm text-muted-foreground">
        {t("wachtwoord.done.codes")}
      </p>
      <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
        {codes.map((recovery) => (
          <li key={recovery}>{recovery}</li>
        ))}
      </ul>
      <Button onClick={onBevestig}>{t("wachtwoord.done.confirm")}</Button>
    </div>
  );
}

/** The starting point before a second factor exists: nothing to enrol against yet but a button. */
function TweedeFactorStartKaart({
  pending,
  onStart,
  fout,
  t,
}: {
  pending: boolean;
  onStart: () => void;
  fout?: string;
  t: Vertaler;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t("wachtwoord.none")}</p>
      <Button disabled={pending} onClick={onStart}>
        {pending ? t("common:actions.busy") : t("wachtwoord.set")}
      </Button>
      {fout && <p className="text-sm text-destructive">{fout}</p>}
    </div>
  );
}

/** The password-and-code form, once an enrolment exists to scan and confirm. */
function TweedeFactorFormulier({
  qr,
  sleutel,
  password,
  onPasswordChange,
  repeat,
  onRepeatChange,
  code,
  onCodeChange,
  fout,
  pending,
  onSubmit,
  t,
}: {
  qr: string;
  sleutel?: string;
  password: string;
  onPasswordChange: (value: string) => void;
  repeat: string;
  onRepeatChange: (value: string) => void;
  code: string;
  onCodeChange: (value: string) => void;
  fout?: string;
  pending: boolean;
  onSubmit: (event: React.FormEvent) => void;
  t: Vertaler;
}) {
  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <p className="text-sm text-muted-foreground">
        {t("wachtwoord.together")}
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="nieuw-wachtwoord">{t("wachtwoord.new")}</Label>
        <Input
          id="nieuw-wachtwoord"
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          value={password}
          onChange={(event) => onPasswordChange(event.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          {t("wachtwoord.policy")}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="herhaal-wachtwoord">{t("wachtwoord.repeat")}</Label>
        <Input
          id="herhaal-wachtwoord"
          type="password"
          autoComplete="new-password"
          required
          value={repeat}
          onChange={(event) => onRepeatChange(event.target.value)}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">{t("wachtwoord.scan")}</p>
        <img
          src={qr}
          alt={t("wachtwoord.qrAlt")}
          className="rounded-md border"
        />
        <p className="text-xs text-muted-foreground">
          {t("wachtwoord.manual", { key: sleutel ?? "" })}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tweede-factor-code">{t("wachtwoord.code")}</Label>
        <Input
          id="tweede-factor-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(event) => onCodeChange(event.target.value)}
        />
      </div>

      {fout && <p className="text-sm text-destructive">{fout}</p>}

      <FormActions>
        <Button type="submit" disabled={pending}>
          {pending ? t("common:actions.busy") : t("wachtwoord.submit")}
        </Button>
      </FormActions>
    </form>
  );
}

/**
 * One transaction: the password and a code together, so abandoning it saves nothing and
 * ADR-0011's rule holds by construction rather than by a check somebody has to remember.
 */
export function SetWachtwoord() {
  const { t } = useTranslation(["account", "common"]);
  const queryClient = useQueryClient();
  const begin = useBeginTweedeFactor();
  const set = useSetWachtwoord();

  const [qr, setQr] = useState<string>();
  const [key, setKey] = useState<string>();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>();

  async function start() {
    setError(undefined);

    try {
      const enrolment = await begin.mutateAsync();
      setKey(enrolment.key);
      setQr(await QRCode.toDataURL(enrolment.uri, { margin: 1, width: 220 }));
    } catch (cause) {
      setError(
        foutmeldingUitOnbekendeFout(
          cause,
          t("errors.tweedeFactor", { status: 0 }),
        ),
      );
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);

    if (password !== repeat) {
      setError(t("wachtwoord.mismatch"));
      return;
    }

    try {
      const result = await set.mutateAsync({ password, code });

      if (result.status === "gezet") {
        setRecoveryCodes(result.recoveryCodes ?? []);
        return;
      }

      setError(message(result.status as SetPasswordStatus, t));
    } catch (cause) {
      setError(
        foutmeldingUitOnbekendeFout(
          cause,
          t("errors.wachtwoord", { status: 0 }),
        ),
      );
    }
  }

  if (recoveryCodes) {
    return (
      <HerstelcodesKaart
        codes={recoveryCodes}
        onBevestig={() =>
          void queryClient.invalidateQueries({ queryKey: accountKey })
        }
        t={t}
      />
    );
  }

  if (!qr) {
    return (
      <TweedeFactorStartKaart
        pending={begin.isPending}
        onStart={() => void start()}
        fout={error}
        t={t}
      />
    );
  }

  return (
    <TweedeFactorFormulier
      qr={qr}
      sleutel={key}
      password={password}
      onPasswordChange={setPassword}
      repeat={repeat}
      onRepeatChange={setRepeat}
      code={code}
      onCodeChange={setCode}
      fout={error}
      pending={set.isPending}
      onSubmit={(event) => void submit(event)}
      t={t}
    />
  );
}

/** A code proves the holder: a live session alone must not strip the second factor. */
function RemoveWachtwoord() {
  const { t } = useTranslation(["account", "common"]);
  const remove = useRemoveWachtwoord();

  const [removeCode, setRemoveCode] = useState("");
  const [removeError, setRemoveError] = useState<string>();

  async function submitRemove(event: React.FormEvent) {
    event.preventDefault();
    setRemoveError("");

    try {
      const result = await remove.mutateAsync({ code: removeCode });

      if (result.status !== "verwijderd") {
        setRemoveError(message(result.status as SetPasswordStatus, t));
      }
    } catch (cause) {
      setRemoveError(
        foutmeldingUitOnbekendeFout(
          cause,
          t("errors.wachtwoord", { status: 0 }),
        ),
      );
    }
  }

  return (
    <form
      className="space-y-3 border-t pt-4"
      onSubmit={(event) => void submitRemove(event)}
    >
      <p className="text-sm text-muted-foreground">
        {t("wachtwoord.removeUitleg")}
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="verwijder-code">{t("wachtwoord.removeCode")}</Label>
        <Input
          id="verwijder-code"
          autoComplete="one-time-code"
          required
          value={removeCode}
          onChange={(event) => setRemoveCode(event.target.value)}
        />
      </div>

      {removeError && <p className="text-sm text-destructive">{removeError}</p>}

      <FormActions>
        <Button type="submit" variant="outline" disabled={remove.isPending}>
          {remove.isPending ? t("common:actions.busy") : t("wachtwoord.remove")}
        </Button>
      </FormActions>
    </form>
  );
}

/** Changing one that exists. No code step: the constraint guarantees a factor is already there. */
export function ChangeWachtwoord() {
  const { t } = useTranslation(["account", "common"]);
  const change = useChangeWachtwoord();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);
    setDone(false);

    try {
      const result = await change.mutateAsync({ current, next });

      if (result.status === "gezet") {
        setCurrent("");
        setNext("");
        setDone(true);
        return;
      }

      setError(message(result.status as SetPasswordStatus, t));
    } catch (cause) {
      setError(
        foutmeldingUitOnbekendeFout(
          cause,
          t("errors.wachtwoord", { status: 0 }),
        ),
      );
    }
  }

  return (
    <div className="space-y-6">
      <form className="space-y-3" onSubmit={(event) => void submit(event)}>
        <div className="space-y-1.5">
          <Label htmlFor="huidig-wachtwoord">{t("wachtwoord.current")}</Label>
          <Input
            id="huidig-wachtwoord"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="volgend-wachtwoord">{t("wachtwoord.new")}</Label>
          <Input
            id="volgend-wachtwoord"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {t("wachtwoord.policy")}
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {done && <p className="text-sm">{t("wachtwoord.changed")}</p>}

        <FormActions>
          <Button type="submit" disabled={change.isPending}>
            {change.isPending
              ? t("common:actions.busy")
              : t("wachtwoord.change")}
          </Button>
        </FormActions>
      </form>

      <RemoveWachtwoord />
    </div>
  );
}
